export default function SimplexExplanation() {
  return (
    <div className="w-full p-1 text-xs text-gray-800 text-justify leading-relaxed hyphens-auto md:text-sm">
      <p>
        <strong>Goal:</strong> Solve linear optimization problems of the form{' '}
         <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1 flex items-center justify-center text-center min-h-[40px]">
            min cᵀx subject to Ax = b, x ≥ 0
          </pre>
      </p>

      <ul className="list-disc pl-6 my-2">
        <li><code>x</code> is the vector of decision variables.</li>
        <li><code>c</code> is the objective coefficient vector.</li>
        <li><code>A</code> is the constraint matrix and <code>b</code> the right-hand side vector.</li>
        <li>Each constraint can have relation ≤, =, or ≥ (the solver standardizes internally).</li>
      </ul>

      <p className="mt-3"><strong>Mathematical explanation</strong></p>
      <p>
        Simplex works on a standardized “simplex form”. Inequalities are converted to equalities
        by adding slack/surplus variables, yielding a system like <code>Ax = b</code> with <code>x ≥ 0</code>.
        The feasible set is a convex polytope, and an optimum (if it exists) is attained at a corner (vertex).
        Simplex therefore moves from vertex to vertex.
      </p>

      <p className="mt-2">
        Each vertex corresponds to a <strong>basis</strong>: a set of variables called <em>basic variables</em>
        that are solved from the constraints, while all remaining <em>nonbasic variables</em> are fixed to zero.
        A <strong>pivot</strong> swaps one nonbasic variable into the basis (entering variable) and one basic variable out
        (leaving variable), preserving feasibility and typically improving the objective.
      </p>

      <p className="mt-2">
        If a feasible starting basis is not obvious, the solver uses the <strong>two-phase method</strong>:
        <strong> Phase I</strong> introduces temporary artificial variables and minimizes their sum to find any feasible solution.
        If Phase I cannot reach zero, the original problem is infeasible. <strong>Phase II</strong> then removes artificial variables,
        restores the original objective, and continues pivoting until optimality is reached.
      </p>

      <p className="mt-4 font-semibold">Input format</p>
<ul className="list-disc pl-5 space-y-2 mb-4 mt-2">
  <li><strong>Objective:</strong> 1D array of coefficients <code>c</code>.</li>
  <li><strong>Matrix A:</strong> 2D array of constraint coefficients.</li>
  <li><strong>Vector b:</strong> Right-hand side values.</li>
  <li><strong>Relations:</strong> Array of symbols (≤, =, ≥) aligned with each constraint.</li>
</ul>

<p className="mb-2"><strong>Example</strong></p>

<pre className="bg-white p-2 border border-gray-200 rounded text-xs mt-1 mb-2">
c = [3,2]
<br />
A = [[1, 1],
<br />
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[2, 1]]
<br />
b = [4, 5]
<br />
relations = ["≤", "≤"]
</pre>

<p className="text-xs">
This corresponds to the problem:
</p>

<pre className="bg-white p-2 border border-gray-200 rounded text-xs mt-1">
min 3x₁ + 2x₂
<br />
subject to
<br />
x₁ + x₂ ≤ 4
<br />
2x₁ + x₂ ≤ 5
<br />
x₁, x₂ ≥ 0
</pre>

      <p className="mt-4 font-semibold">Output format</p>
      <ul className="list-disc pl-5 mb-4">
        <li>Phase I and Phase II tableau iterations are listed step-by-step.</li>
        <li>Each step notes the entering/leaving variables and pivot element.</li>
        <li>The final summary provides the optimal solution and objective value (or infeasible/unbounded).</li>
      </ul>
    </div>
  );
}
